/** Research spreadsheet import UI. Business validation and idempotency remain server-side. */
export const RESEARCH_IMPORT_HEADERS = [
    'title', 'author', 'posting_date', 'journal', 'conference', 'volume',
    'number', 'pages', 'issn', 'impact_factor', 'link'
];
export const RESEARCH_IMPORT_MAX_BYTES = 10 * 1024 * 1024;

export class ResearchImportState {
    public busy = false;
    public fileName = '';
    public message = '';
    public errors: { row: number | null; field: string; message: string }[] = [];
    public result: any = null;
    public retryable = false;
    public needsFile = false;
    public refreshWarning = false;
    public stage = '';
    private file: File | null = null;
    private uploadedUrl = '';
    private destroyed = false;

    constructor(private service: any, private call: (params: { url: string }) => Promise<any>,
                private onSuccess: () => Promise<void>) { }

    public choose(input: HTMLInputElement) {
        if (this.busy || this.destroyed) return;
        input.value = '';
        input.click();
    }

    public async upload(input: HTMLInputElement) {
        const files = Array.from(input.files || []);
        input.value = '';
        if (this.busy || this.destroyed || !files.length) return;
        this.file = null;
        this.uploadedUrl = '';
        this.fileName = files.map(file => file.name).join(', ');
        this.clear();
        if (files.length !== 1) return this.invalid('파일은 한 번에 하나만 선택해주세요.');
        const file = files[0];
        if (!/\.(xlsx|csv|xls)$/i.test(file.name)) return this.invalid('XLSX, CSV, XLS 파일만 업로드할 수 있습니다.');
        if (!file.size) return this.invalid('빈 파일은 업로드할 수 없습니다.');
        if (file.size > RESEARCH_IMPORT_MAX_BYTES) return this.invalid('파일 크기는 10 MiB 이하여야 합니다.');
        this.file = file;
        await this.run();
    }

    public async retry() {
        if (!this.retryable || this.busy || this.destroyed) return;
        await this.run();
    }

    private clear() {
        this.message = '';
        this.errors = [];
        this.result = null;
        this.retryable = false;
        this.needsFile = false;
        this.refreshWarning = false;
    }

    private async invalid(message: string) {
        this.message = message;
        this.needsFile = true;
        await this.render();
    }

    private async render() {
        if (this.destroyed) return;
        try { await this.service.render(); } catch (_) { }
    }

    private async run() {
        if (this.busy || this.destroyed || (!this.file && !this.uploadedUrl)) return;
        this.busy = true;
        this.clear();
        this.stage = this.uploadedUrl ? 'importing' : 'uploading';
        try {
            await this.render();
            await this.service.loading.show();
            if (this.destroyed) return;
            if (!this.uploadedUrl) {
                const form = new FormData();
                form.append('upload', this.file!);
                const uploaded = await this.service.file.upload('/file/upload', form);
                if (this.destroyed) return;
                // The shared uploader resolves HTTP failures too; never forward an error object.
                if (typeof uploaded?.url !== 'string' || !/^\/file\/download\/[^/?#]+\/[^?#]+$/.test(uploaded.url))
                    throw new Error('UPLOAD_UNCONFIRMED');
                this.uploadedUrl = uploaded.url;
            }
            this.stage = 'importing';
            await this.render();
            const response = await this.call({ url: this.uploadedUrl });
            if (this.destroyed) return;
            const code = Number(response?.code);
            const data = response?.data;
            if (code === 200) {
                const fields = ['imported', 'already_imported', 'skipped_blank', 'total_rows'];
                if (!data || fields.some(key => !Number.isSafeInteger(data[key]) || data[key] < 0))
                    throw new Error('IMPORT_RESULT_UNCONFIRMED');
                this.result = Object.fromEntries(fields.map(key => [key, data[key]]));
                this.message = '성과 업로드가 완료되었습니다.';
                this.file = null;
                await this.refresh();
            } else {
                this.message = typeof data === 'string' ? data :
                    (typeof data?.message === 'string' ? data.message : '성과 파일을 처리하지 못했습니다.');
                this.errors = Array.isArray(data?.errors) ? data.errors
                    .filter((item: any) => item && typeof item.message === 'string')
                    .map((item: any) => ({
                        row: Number.isSafeInteger(item.row) && item.row > 0 ? item.row : null,
                        field: typeof item.field === 'string' ? item.field : '', message: item.message
                    })) : [];
                this.retryable = !Number.isFinite(code) || code === 0 || code >= 500;
                this.needsFile = code === 400 || code === 404;
            }
        } catch (_) {
            if (!this.destroyed) {
                this.message = this.stage === 'uploading'
                    ? '파일 전송에 실패했습니다. 같은 파일로 다시 시도해주세요.'
                    : '등록 결과를 확인하지 못했습니다. 같은 파일로 다시 시도해주세요.';
                this.retryable = true;
            }
        } finally {
            this.busy = false;
            this.stage = '';
            try { await this.service.loading.hide(); } catch (_) { }
            await this.render();
        }
    }

    public async refresh() {
        if (this.destroyed || !this.result) return;
        this.refreshWarning = false;
        try { await this.onSuccess(); } catch (_) { this.refreshWarning = true; }
        await this.render();
    }

    public downloadTemplate() {
        const blob = new Blob(['\uFEFF' + RESEARCH_IMPORT_HEADERS.join(',') + '\r\n'], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'research-import-template.csv';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    public dispose() {
        this.destroyed = true;
        this.file = null;
    }
}
